// @ts-nocheck
// Illuminazione giorno/notte basata sull'ora locale del browser.
//
// Un overlay a tutto schermo (#night-overlay, mix-blend-mode: multiply) tinge e
// scurisce l'intera scena del cimitero: luce naturale di giorno, toni caldi
// all'alba/tramonto, indaco profondo di notte. L'opacità dell'overlay segue una
// curva continua sull'ora locale (`new Date()` riflette già il fuso del
// browser), aggiornata ogni minuto.
//
// In più espone la variabile CSS globale `--ambient-night` (0 = giorno, 1 =
// notte fonda): gli aloni delle candele (vedi candle-glow.ts + tombstone.css)
// la usano per comparire solo di notte, scalati dal numero di candele — così le
// lapidi più "visitate" si vedono di più nel buio.
//
// Debug: `?time=23` (o `?time=18.5`) nell'URL forza un'ora; in console
// `setSceneTime(23)` la cambia a runtime, `setSceneTime(null)` torna all'ora
// reale.

// Flag per il ciclo giorno/notte: mettere a true per riattivare
// l'illuminazione automatica basata sull'ora locale.
const DAY_NIGHT_CYCLE_ENABLED = false;

const NIGHT_COLOR = [18, 22, 64]; // indaco profondo (notte fonda)
const WARM_COLOR = [255, 138, 58]; // arancio caldo (alba / tramonto)
const MAX_ALPHA = 0.82; // opacità massima dell'overlay a notte fonda

// Punti di controllo (oraLocale -> valore), interpolati linearmente.
// darkness: 0 = pieno giorno, 1 = notte fonda.
const DARKNESS_POINTS = [
  [0, 1.0],
  [5, 1.0],
  [6, 0.7],
  [7, 0.35],
  [8.5, 0.0],
  [17, 0.0],
  [18.5, 0.4],
  [20, 0.78],
  [21.5, 1.0],
  [24, 1.0],
];

// warmth: 0 = tinta fredda (notte/giorno), 1 = tinta calda (alba/tramonto).
const WARMTH_POINTS = [
  [0, 0.0],
  [5, 0.0],
  [6, 0.5],
  [7, 1.0],
  [8.5, 0.2],
  [17, 0.2],
  [18, 0.9],
  [19, 1.0],
  [20, 0.5],
  [21.5, 0.0],
  [24, 0.0],
];

function interp(points, h) {
  if (h <= points[0][0]) return points[0][1];
  const last = points[points.length - 1];
  if (h >= last[0]) return last[1];
  for (let i = 1; i < points.length; i++) {
    const [x0, y0] = points[i - 1];
    const [x1, y1] = points[i];
    if (h <= x1) {
      const t = (h - x0) / (x1 - x0);
      return y0 + (y1 - y0) * t;
    }
  }
  return last[1];
}

function mix(a, b, t) {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ];
}

let overlayEl = null;
let timer = null;
let overrideHour = null;

function ensureOverlay() {
  if (overlayEl && document.body && document.body.contains(overlayEl)) {
    return overlayEl;
  }
  const existing = document.getElementById("night-overlay");
  if (existing) {
    overlayEl = existing;
    return overlayEl;
  }
  const viewport = document.getElementById("viewport");
  if (!viewport) return null;
  overlayEl = document.createElement("div");
  overlayEl.id = "night-overlay";
  overlayEl.setAttribute("aria-hidden", "true");
  viewport.appendChild(overlayEl);
  return overlayEl;
}

function currentHour() {
  if (overrideHour != null) return overrideHour;
  const now = new Date();
  return now.getHours() + now.getMinutes() / 60;
}

export function applyAmbientForHour(h) {
  const darkness = interp(DARKNESS_POINTS, h);
  const warmth = interp(WARMTH_POINTS, h);
  const color = mix(NIGHT_COLOR, WARM_COLOR, warmth);

  document.documentElement.style.setProperty(
    "--ambient-night",
    darkness.toFixed(3),
  );

  const overlay = ensureOverlay();
  if (overlay) {
    overlay.style.background = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
    overlay.style.opacity = (darkness * MAX_ALPHA).toFixed(3);
  }
}

function tick() {
  applyAmbientForHour(currentHour());
}

export function initDayNight() {
  if (!DAY_NIGHT_CYCLE_ENABLED) {
    // Pieno giorno fisso: niente overlay, niente timer, candele senza alone.
    document.documentElement.style.setProperty("--ambient-night", "0");
    document.getElementById("night-overlay")?.remove();
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
    return;
  }

  // Override di debug via query string.
  try {
    const param = new URLSearchParams(window.location.search).get("time");
    if (param != null && param !== "") {
      const h = parseFloat(param);
      if (!Number.isNaN(h)) overrideHour = ((h % 24) + 24) % 24;
    }
  } catch {
    /* no-op */
  }

  // Helper di debug a runtime.
  window.setSceneTime = (h) => {
    if (h == null) {
      overrideHour = null;
    } else {
      const n = parseFloat(h);
      overrideHour = Number.isNaN(n) ? null : ((n % 24) + 24) % 24;
    }
    tick();
  };

  ensureOverlay();
  tick();
  if (timer) clearInterval(timer);
  // L'illuminazione cambia lentamente: basta un aggiornamento al minuto.
  timer = setInterval(tick, 60_000);
}
